import React from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';

/**
 * Add this type in top of your file, or if commonly used in some types file.
 * @template T
 * @typedef {[T, import('react').Dispatch<import('react').SetStateAction<T>>]} useState
 */

/**
 * Especifica la función a ser ejecutada al hacer click en el botón principal
 * @callback OnClickButton
 * @returns {void}
 */

/**
 * Especifica la función a ser ejecutada al hacer click en opción de botón
 * @callback OnClickOptionButton
 * @returns {void}
 */

/**
 * Opción de menú
 * @typedef {Object} ButtonOption
 * @property {number} id Id de opción
 * @property {*} option Opción
 * @property {boolean?} disabled Si es `true`, la opción será deshabilitada
 * @property {OnClickOptionButton} onClickOption Especifica la función a ser ejecutada al hacer click en una opción
 */

/**
 * Props de botón de división con menú desplegable
 * @typedef {Object} Props
 * @property {string} buttonText Texto de botón
 * @property {*} startIcon Icono inicial de botón
 * @property {boolean?} disabledBtn Si es `true`, el botón principal será deshabilitada
 * @property {boolean?} disabledOpt Si es `true`, el botón secundario será deshabilitada
 * @property {boolean?} hiddenBtn Si es `true`, el botón principal será ocultado
 * @property {boolean?} hiddenOpt Si es `true`, el botón secundario será ocultado
 * @property {ButtonOption[]} options Opciones de menú
 * @property {OnClickButton} onButtonClick Especifica la función a ser ejecutada al hacer click en el botón principal
 */

/**
 * Botón de división con menú desplegable
 * @param {Props} props
 * @param {ChildrenFn} props.children
 * @returns 
 */
const SplitButton = ({buttonText = "", startIcon, disabledBtn = false, hiddenBtn = false, disabledOpt = false, hiddenOpt = false, options = [], onButtonClick = () => {}}) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleMenuItemClick = (event, index) => {
    setOpen(false);
    if (options[index]?.onClickOption) options[index]?.onClickOption();
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  return (
    <Grid container direction="column" alignItems="center">
      <Grid item xs={12}>
        <ButtonGroup variant="contained" disableElevation color="primary" ref={anchorRef} aria-label="split button">
          {!hiddenBtn && <Button startIcon={startIcon} disabled={disabledBtn} onClick={onButtonClick}>{buttonText}</Button>}
          {!hiddenOpt && <Button
            color="primary"
            size={!hiddenBtn ? "small" : undefined}
            startIcon={hiddenBtn ? startIcon : undefined}
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select option"
            aria-haspopup="menu"
            disabled={disabledOpt}
            onClick={handleToggle}
          >
            {hiddenBtn ? buttonText : <SettingsOutlinedIcon />}
          </Button>}
        </ButtonGroup>
        <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu">
                    {options.map((option, index) => (
                      <MenuItem
                        key={option.id}
                        disabled={option.disabled}
                        onClick={(event) => handleMenuItemClick(event, index)}
                      >
                        {option.option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>
    </Grid>
  );
};

export default SplitButton;